import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { SelectField } from '@mews-commander/form';
import { FormInputField, FormCheckboxField, FormNumberInputField, lt } from '@mews/framework';
import { Grid, Stack, Accordion, Typography } from '@optimus-web/core';
import { useAccountingCategoriesOptions } from '../../hooks/useAccountingCategoriesOptions';
import { useCurrentEnterpriseTaxRateOptions } from '../../hooks/useCurrentEnterpriseTaxCodeOptions';
import { FieldNames } from '../fieldNames';
import { XmlPosOutletFormSchema } from '../microsOutlets/formProps';
import { AccountingCategoryGrid } from '../AccountingCategoryGrid';

interface ServingtimeMappingFormProps {
    index: number;
}

export const ServingtimeMappingForm = ({ index }: ServingtimeMappingFormProps) => {
    const { setValue } = useFormContext<XmlPosOutletFormSchema>();
    const { accountingCategoriesOptions } = useAccountingCategoriesOptions();
    const servicetimeMappingIndex = `${FieldNames.serviceTimes}.${index}`;
    const allTaxRates = useCurrentEnterpriseTaxRateOptions();

    const { fields: serviceTimesFields } = useFieldArray<XmlPosOutletFormSchema, 'serviceTimes'>({
        name: FieldNames.serviceTimes,
    });
    const { fields: netSalesFields } = useFieldArray({
        name: `${servicetimeMappingIndex}.${FieldNames.netSalesAccountingCategories}`,
    });
    const { fields: discountFields } = useFieldArray({
        name: `${servicetimeMappingIndex}.${FieldNames.discountAccountingCategories}`,
    });
    const { fields: serviceChargeFields } = useFieldArray({
        name: `${servicetimeMappingIndex}.${FieldNames.serviceChargeAccountingCategories}`,
    });

    const [expanded, setExpanded] = useState(false);

    const content = (
        <Stack spacing="300" vertical>
            {netSalesFields.map((field, indexField) => (
                <AccountingCategoryGrid key={field.id}>
                    <SelectField
                        name={`${servicetimeMappingIndex}.${FieldNames.netSalesAccountingCategories}.${indexField}.${FieldNames.categoryId}`}
                        label={
                            <>
                                {lt('PosSaleAccountingCategory')}
                                <span>{` ${indexField + 1}`}</span>
                            </>
                        }
                        options={accountingCategoriesOptions}
                    />
                    <SelectField
                        name={`${servicetimeMappingIndex}.${FieldNames.netSalesAccountingCategories}.${indexField}.${FieldNames.taxCodes}`}
                        label={lt('TaxCodes')}
                        options={allTaxRates}
                        multiselect
                    />
                </AccountingCategoryGrid>
            ))}
            {discountFields.map(
                (field, indexField) =>
                    indexField < 16 && (
                        <AccountingCategoryGrid key={field.id}>
                            <SelectField
                                name={`${servicetimeMappingIndex}.${FieldNames.discountAccountingCategories}.${indexField}.${FieldNames.categoryId}`}
                                label={
                                    <>
                                        {lt('DiscountAccountingCategory')}
                                        <span>{` ${indexField + 1}`}</span>
                                    </>
                                }
                                options={accountingCategoriesOptions}
                            />
                            <SelectField
                                name={`${servicetimeMappingIndex}.${FieldNames.discountAccountingCategories}.${indexField}.${FieldNames.taxCodes}`}
                                label={lt('TaxCodes')}
                                options={allTaxRates}
                                multiselect
                            />
                        </AccountingCategoryGrid>
                    )
            )}
            {serviceChargeFields.map(
                (field, indexField) =>
                    indexField < 16 && (
                        <AccountingCategoryGrid key={field.id}>
                            <SelectField
                                name={`${servicetimeMappingIndex}.${FieldNames.serviceChargeAccountingCategories}.${indexField}.${FieldNames.categoryId}`}
                                label={
                                    <>
                                        {lt('ServiceChargeAccountingCategory')}
                                        <span>{` ${indexField + 1}`}</span>
                                    </>
                                }
                                options={accountingCategoriesOptions}
                            />
                            <SelectField
                                name={`${servicetimeMappingIndex}.${FieldNames.serviceChargeAccountingCategories}.${indexField}.${FieldNames.taxCodes}`}
                                label={lt('TaxCodes')}
                                options={allTaxRates}
                                multiselect
                            />
                        </AccountingCategoryGrid>
                    )
            )}
            <Grid
                columnGap="200"
                templateRows="repeat(auto-fit, minmax(0, 2fr), minmax(0, 2fr))"
                templateColumns="minmax(0, 2fr) minmax(0, 2fr)"
                height="100%"
                tabletOnly={{
                    templateColumns: 'minmax(0, 4fr)',
                }}
                largePhoneOnly={{
                    templateColumns: 'minmax(0, 2fr)',
                }}
                phoneOnly={{
                    templateColumns: 'minmax(0, 1fr)',
                }}
            >
                <SelectField
                    name={`${servicetimeMappingIndex}.${FieldNames.tipId}`}
                    label={lt('TipsAccountingCategory')}
                    options={accountingCategoriesOptions}
                />
                <SelectField
                    name={`${servicetimeMappingIndex}.${FieldNames.tipsAccountingCategoryTaxCodes}`}
                    label={lt('TaxCodes')}
                    multiselect
                    options={allTaxRates}
                />
            </Grid>
        </Stack>
    );
    const handleOnChangeDefault = (checked: boolean) => {
        serviceTimesFields.forEach((_field, indexField) => {
            if (indexField !== index || !checked) {
                setValue(`${FieldNames.serviceTimes}.${indexField}.${FieldNames.isDefault}`, false, {
                    shouldDirty: true,
                });
            }
        });
    };

    return (
        <Stack vertical spacing="200">
            <FormCheckboxField
                name={`${servicetimeMappingIndex}.${FieldNames.isDefault}`}
                onChange={(event, checked) => {
                    handleOnChangeDefault(checked);
                }}
            >
                {lt('Default')}
            </FormCheckboxField>
            <Grid
                columnGap="200"
                templateRows="repeat(auto-fit, minmax(0, 2fr), minmax(0, 2fr), minmax(0, 2fr))"
                templateColumns="minmax(0, 2fr) minmax(0, 2fr) minmax(0, 2fr)"
                height="100%"
                tabletOnly={{
                    templateColumns: 'minmax(0, 3fr) minmax(0, 3fr)',
                    templateRows: 'repeat(auto-fit, minmax(0, 3fr), minmax(0, 3fr))',
                }}
                largePhoneOnly={{
                    templateColumns: 'minmax(0, 2fr)',
                }}
                phoneOnly={{
                    templateColumns: 'minmax(0, 1fr)',
                }}
            >
                <FormNumberInputField
                    size="medium"
                    name={`${servicetimeMappingIndex}.${FieldNames.time}`}
                    label={lt('PosServingTimeId')}
                />
                <FormInputField
                    name={`${servicetimeMappingIndex}.${FieldNames.name}`}
                    label={lt('PosServingTimeName')}
                />
                <FormInputField
                    name={`${servicetimeMappingIndex}.${FieldNames.timeId}`}
                    label={lt('ServiceKey')}
                    required
                />
            </Grid>
            <Accordion
                bodyPadding="0"
                expanded={expanded}
                headerPadding={{ bottom: '200', left: '0', right: '0' }}
                onClick={() => setExpanded(!expanded)}
                body={content}
                title={
                    <Typography textStyle="bodyLargeStrong" as="div">
                        {lt('RoutingRuleDetailAccountingCategories')}
                    </Typography>
                }
                withBodyBorder={false}
                withHeaderBorder={false}
            />
        </Stack>
    );
};
